/**
 * Options (docs/spec/07 §5): every setting, grouped, left/right to change, applied at once, one
 * line explaining the selected row. Also opened from the pause menu; BACK returns there.
 */

import type { Options } from '../../platform/options';
import type { Screen } from '../../render/screen';
import type { MenuAction, PointerInput } from '../scene';
import { UiScene } from '../kit/base';
import { drawOptionRow } from '../kit/optionRow';
import { drawRasterRule } from '../kit/raster';
import { FAINT, HEADING, INK, MUTED, RAMPS } from '../kit/theme';
import type { OptionLine, OptionRow } from './optionsModel';
import { lineOfRow, OPTION_ROWS, optionLines } from './optionsModel';

const TOP = 27;
const LINE_H = 9;
const VISIBLE = 15;
const COLS = { label: 38, value: 188 };
const BAR_X = COLS.value;

/** Remembered while the page is open, so returning from Controls lands where you were. */
let lastRow = 0;

function webgl2(): boolean {
  return typeof WebGL2RenderingContext !== 'undefined';
}

export class OptionsScene extends UiScene {
  protected readonly heading = 'OPTIONS';
  /** Options can sit over a paused game: leave its music alone. */
  protected override readonly track = null;
  private readonly lines: OptionLine[] = optionLines();
  private row = lastRow;
  private scroll = 0;
  private scrollPos = 0;

  private get current(): OptionRow | undefined {
    return OPTION_ROWS[this.row];
  }

  override enter(): void {
    super.enter();
    this.follow();
    this.scrollPos = this.scroll;
  }

  protected override describe(): string {
    return `Options. ${this.rowText()}`;
  }

  private rowText(): string {
    const r = this.current;
    if (!r) return '';
    const v = r.value(this.services.options.get());
    const text = v.kind === 'choice' ? v.text : v.kind === 'level' ? String(v.value) : 'opens a screen';
    return `${r.label}: ${text}. ${r.help}`;
  }

  protected override hint(): string {
    return `◄► CHANGE   ▲▼ MOVE   ${this.words().back} BACK`;
  }

  /** Scroll so the selected row, and its group's heading, are in view. */
  private follow(): void {
    const line = lineOfRow(this.lines, this.row);
    let s = this.scroll;
    const top = this.lines[line - 1]?.kind === 'group' ? line - 1 : line;
    if (top < s) s = top;
    if (line >= s + VISIBLE) s = line - VISIBLE + 1;
    this.scroll = Math.max(0, Math.min(this.lines.length - VISIBLE, s));
  }

  protected override tick(dt: number): void {
    this.scrollPos += (this.scroll - this.scrollPos) * Math.min(1, dt / 70);
    if (Math.abs(this.scroll - this.scrollPos) < 0.02) this.scrollPos = this.scroll;
  }

  protected paint(screen: Screen): void {
    const o = this.services.options.get();
    const ctx = screen.ctx;
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, TOP - 3, screen.width, VISIBLE * LINE_H + 1);
    ctx.clip();
    this.lines.forEach((line, i) => {
      const y = Math.round(TOP + (i - this.scrollPos) * LINE_H);
      if (y < TOP - LINE_H || y > TOP + VISIBLE * LINE_H) return;
      if (line.kind === 'group') {
        screen.text(line.label, 12, y, HEADING);
        screen.fill(12 + line.label.length * 8 + 6, y + 3, 294 - line.label.length * 8 - 6, 1, FAINT);
        return;
      }
      const row = OPTION_ROWS[line.row];
      if (row) drawOptionRow(screen, y, row.label, row.value(o), line.row === this.row, this.ms, COLS);
    });
    ctx.restore();
    this.paintScrollbar(screen);
    drawRasterRule(screen, 166, this.ms, RAMPS.grey, 8, 312);
    const r = this.current;
    let help = r?.help ?? '';
    if (r?.id === 'crt' && !webgl2()) help = 'NO WEBGL2 HERE, SO THE TV LOOK IS OFF.';
    screen.text(help, 160, 174, INK, 'center');
  }

  private paintScrollbar(screen: Screen): void {
    const h = VISIBLE * LINE_H;
    const y0 = TOP - 2;
    screen.fill(314, y0, 1, h, FAINT);
    const size = Math.max(8, Math.round((h * VISIBLE) / this.lines.length));
    const at = Math.round(((h - size) * this.scrollPos) / Math.max(1, this.lines.length - VISIBLE));
    screen.fill(313, y0 + at, 3, size, MUTED);
  }

  private select(row: number): void {
    const next = Math.max(0, Math.min(OPTION_ROWS.length - 1, row));
    if (next === this.row) return;
    this.row = next;
    lastRow = next;
    this.follow();
    this.sound('move');
    this.say(this.rowText());
  }

  private change(next: Options | null): void {
    if (!next || JSON.stringify(next) === JSON.stringify(this.services.options.get())) return this.sound('error');
    this.services.options.update(next);
    this.sound('move');
    this.say(this.rowText());
  }

  private open(): void {
    this.sound('select');
    this.host.push({ to: 'controls' });
  }

  protected onAction(a: MenuAction): void {
    const r = this.current;
    if (a === 'up') this.select(this.row === 0 ? OPTION_ROWS.length - 1 : this.row - 1);
    else if (a === 'down') this.select(this.row === OPTION_ROWS.length - 1 ? 0 : this.row + 1);
    else if (!r) return;
    else if (a === 'left' || a === 'right') {
      if (!r.step) return a === 'right' ? this.open() : this.sound('error');
      this.change(r.step(this.services.options.get(), a === 'left' ? -1 : 1));
    } else if (a === 'confirm') {
      if (!r.step) this.open();
      else this.change(r.step(this.services.options.get(), 1));
    }
  }

  protected override onPointer(p: PointerInput): void {
    if (p.kind === 'wheel') return this.select(this.row + p.delta);
    if (p.y < TOP - 3 || p.y >= TOP - 3 + VISIBLE * LINE_H) return;
    const i = Math.floor((p.y - (TOP - 1) + this.scrollPos * LINE_H) / LINE_H);
    const line = this.lines[i];
    if (!line || line.kind !== 'row') return;
    if (p.kind === 'move') return this.select(line.row);
    if (p.kind !== 'down' || p.button !== 0) return;
    this.select(line.row);
    const r = this.current;
    if (!r) return;
    if (!r.step) return this.open();
    const o = this.services.options.get();
    const v = r.value(o);
    if (v.kind === 'level' && r.set && p.x >= BAR_X && p.x < BAR_X + v.max * 7) {
      return this.change(r.set(o, Math.floor((p.x - BAR_X) / 7) + 1));
    }
    this.change(r.step(o, p.x < COLS.value - 2 && v.kind === 'level' ? -1 : 1));
  }
}
